// The requisition-scoped candidate page (UAT D-13, CA-2; lane B3-FU): `#requisitions/R-####/candidates/<application>` in
// the Mission Control design. "Each application retains its own stage, status, scorecards, communications, offer
// outcome, and disposition." views/b3/register.ts claims exactly this route, above B2's requisition detail and never
// deeper: `…/rounds/<n>` stays V1's round page, `…/schedule` B5's Final Interview Loop page, and the Candidates tab
// FU-TAB's board.
//
// The page names the candidate in his words (GLOSSARY §22): the name, the Candidate ID beside it, never the opaque
// token. The header carries the stage as the Pipeline places it (his DC-31 column; Held stays in its column with its
// own pill, and a Pass, a withdrawal or a close is the *Passed* strip); the Stage card draws the seven columns as a
// track and the application's stage history in the design system's History; the rail's Application card holds its
// Requisition, Source, the date it was Sourced, its Stage with the board's aging line (and the hold reason while it is
// held), its status and the contacts the seat may read (absent, never masked, for a seat below them); the assessment
// chain is the design system's card it already was.
//
// Re-host, never drop. Every act V1's application page (views/Candidates.tsx, which this page never edits) offers the
// seat stays here with its V1 markup, so a walk's selectors hold: the act buttons and the round links, Record what
// already happened, the furthest stage, batch and disposition, the résumé upload and its versions, Requirements met with
// Qualify, the decisions, holds, messages and withdrawals the acts record, the rounds table, Flag as internal / referral
// and every dialog. They have no word of his yet, so they render through the shell's V1 slot (shell/island.tsx), outside
// the view's `.mc` island with their V1 look, as views/b2/register.ts hosts RecordAfterFact; they move into the body as
// his words for them arrive. V1's *Recruiter's read* is not drawn (DS §3.4, Q-E24). The HR Business Partner's
// projection keeps its V1 record (views/HrbpApplication.tsx), as the Interviewer's projection keeps V1's requisition page.
//
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useState, type ReactNode } from "react";
import {
  candidateAttachResume, candidateDecideAdvance, candidateDispositionEarly, candidateHold, candidateLogMessage, candidateMarkInternal, candidateOpen,
  candidateReinstate, candidateRelease, candidateReverseDecision, candidateWithdraw, type ApplicationFull, type CandidateCloseAtSourceInput,
  type CandidateOpenOutput, type FileUploadOutput,
} from "@/api/tools";
import { isClientRefusal, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { useShellCrumbs } from "@/shell/crumbs";
import { V1Slot } from "@/shell/island";
import type { ViewProps } from "@/shell/viewRegistry";
import { useViewState } from "@/shell/viewState";
import { ArchivedRecord } from "@/ui/ArchivedRecord";
import { DOCUMENT_TYPES, FileField, FileList, type FilePhase, type FileVersion } from "@/ui/FileField";
import { ObjectReadGuard } from "@/ui/ObjectReadGuard";
import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { MISSING, Banner, Empty, Field, KV, Pill as V1Pill, ReasonDialog, Receipt, Table, fmtDate } from "@/ui";
import { RequirementsSection } from "@/views/CandidateRequirements";
import { HrbpApplicationRecord } from "@/views/HrbpApplication";
import { APPLICATION_LABELS as A } from "@/views/application/labels";
import { AssessmentChain } from "@/views/plan/AssessmentChain";
import { useSettledRead } from "@/views/shared/settledRead";
import {
  APPLICATION_STATUS, DEFAULT_TIME_ZONE, ErrorStrip, Flex, G6_TEMPLATES, HERO_LABELS, HISTORY_LABELS, HM_REVIEW_STATUS, History,
  IdPill, KeyValue, METRIC_TEMPLATES, formatDate, NAV_ITEM_LABELS, PageHeader, Pill, Rail, RULE_LABELS, Skeleton, Split, Stack, TextLink, Card,
  type HistoryEvent, type KeyValueItem,
} from "@/mc";
import { BRD } from "./brd";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P } from "./labels";
import { useSourceWord } from "./options";
import { applicationStatus, dispositionWord, stageTrack, stageWord } from "./profile";

/** The registered route (views/b3/register.ts): `#requisitions/R-####/candidates/<application>`. */
export function ApplicationRoute({ route }: ViewProps) {
  const [rid = "", , aid = ""] = route.parts;
  return <ApplicationPage key={`${rid}/${aid}`} rid={rid} aid={aid} />;
}

/** The masked candidate's token label (views/Candidates.tsx's convention): the word Candidate and the token's tail. */
const nameOf = (card: { token: string; display_name?: string | null | undefined }): string =>
  card.display_name ?? `Candidate ${card.token.slice(-4).toUpperCase()}`;

const dash = (value: string | null | undefined): string => (value ? value : "—");

export function ApplicationPage({ rid, aid }: { rid: string; aid: string }) {
  const { register } = useViewState();
  // the same record key V1's page and its writes use, so every act refreshes this read
  const q = useSettledRead<CandidateOpenOutput>(["application", aid, rid], () => candidateOpen({ requisition_id: rid, application_id: aid }));
  const a = q.data;
  useEffect(
    () => register({ view: "application", ids: { requisition: rid, application: aid }, summary: `application ${aid} ${a?.card.stage ?? ""}` }),
    [register, rid, aid, a?.card.stage],
  );
  useShellCrumbs(a ? [{ label: rid, href: `#requisitions/${rid}/candidates` }, { label: nameOf(a.card) }] : null);
  // a refusal is the canonical not-found or no-access screen (§3.3), with nothing of the record mounted
  if (q.error && isClientRefusal(q.error)) return <ObjectReadGuard query={q} />;
  if (q.error) {
    return (
      <ErrorStrip
        message={q.error.remedy || RULE_LABELS.failedSyncRetryable}
        problem={{ status: q.error.status, refusal_code: q.error.code, request_id: q.error.requestId }}
        onRetry={() => void q.refetch()}
        retrying={q.isFetching}
        
      />
    );
  }
  if (!a) {
    return (
      <Stack gap={16}>
        <Skeleton height={72} />
        <Skeleton lines={6} height={18} />
      </Stack>
    );
  }
  // the HR Business Partner's projection carries no rounds: its V1 review record, by the token label
  if (!("rounds" in a)) {
    return <V1Slot><ArchivedRecord record={a}><HrbpApplicationRecord rid={rid} aid={aid} application={a} /></ArchivedRecord></V1Slot>;
  }
  return (
    <>
      <V1Slot><ArchivedRecord record={a}><V1Acts rid={rid} aid={aid} a={a} /></ArchivedRecord></V1Slot>
      <Body rid={rid} aid={aid} a={a} />
    </>
  );
}

// ---- the Mission Control body ------------------------------------------------------------------------------------------

function Body({ rid, aid, a }: { rid: string; aid: string; a: ApplicationFull }) {
  const { me } = useSession();
  const sourceWord = useSourceWord();
  const timeZone = me?.timezone || DEFAULT_TIME_ZONE;
  const c = a.card;
  const name = nameOf(c);
  const read = { stage: c.stage, held_from_stage: c.held_from_stage };
  const stage = stageWord(read);
  const track = stageTrack(read);
  const held = c.stage === "held";
  const heldPill = [HM_REVIEW_STATUS.held, c.held_days != null ? METRIC_TEMPLATES.nDays({ n: c.held_days }) : null, c.hold_reason_label].filter(Boolean).join(" · ");
  const items: KeyValueItem[] = [
    [BRD.requisition, rid],
    [L.source, dash(sourceWord(c.source_channel))],
    [APPLICATION_STATUS.sourced, formatDate(c.entered_pipeline_at, timeZone)],
    [A.stage, (
      <Stack gap={2} key="stage">
        <span>{dash(stage)}</span>
        <span>{G6_TEMPLATES.inStage({ n: c.waiting_business_days })}</span>
      </Stack>
    )],
    ...(held ? [[A.holdReason, dash(c.hold_reason_label ?? c.hold_reason?.replace(/_/g, " "))] as KeyValueItem] : []),
    [L.status, dash(applicationStatus(c.stage))],
    ...(a.email ? [[L.primaryEmail, a.email] as KeyValueItem] : []),
    ...(a.phone ? [[L.phone, a.phone] as KeyValueItem] : []),
  ];
  const main = (
    <>
      <Card title={A.stage}>
        <Stack gap={12}>
          {track ? <Rail label={`${A.stage} · ${rid}`} steps={track} wrap /> : null}
          <History label={HISTORY_LABELS.history} events={historyEvents(a, me ? { id: me.person_id, name: me.name } : null)} timeZone={timeZone} />
        </Stack>
      </Card>
      <AssessmentChain rid={rid} aid={aid} />
    </>
  );
  return (
    <Stack gap={16}>
      <Flex gap={8}><TextLink href={`#requisitions/${rid}/candidates`} >{`${rid} · ${NAV_ITEM_LABELS.candidates}`}</TextLink></Flex>
      <PageHeader
        title={name}
        pills={(
          <>
            {stage ? <Pill tone={held ? "info" : undefined}>{stage}</Pill> : null}
            {held ? <Pill tone="warn">{heldPill}</Pill> : null}
            {c.internal ? <Pill tone="violet">{HERO_LABELS.internal}</Pill> : null}
            {a.candidate_no ? <IdPill value={a.candidate_no}  /> : null}
          </>
        )}
      />
      <Split main={main} side={<Card title={P.application}><KeyValue items={items} cols={1} /></Card>} />
    </Stack>
  );
}

/** The stage history, newest first: each move is the column it reached, by whom, with the reason given. */
function historyEvents(a: ApplicationFull, me: { id: string; name: string } | null): HistoryEvent[] {
  return [...a.stage_history].reverse().map((event, index) => {
    const to = String(event["to"] ?? "");
    const word = stageWord({ stage: to }) ?? "—";
    const by = event["by_name"] ?? (me && event["by"] === me.id ? me.name : null);
    const reason = event["reason"] ? String(event["reason"]) : null;
    return {
      id: `${index}-${to}`,
      actor: by ? { name: String(by) } : undefined,
      sentence: reason ? <>{word}{" · "}{reason}</> : word,
      at: String(event["at"] ?? ""),
      tone: to === "held" ? "warn" : undefined,
    };
  });
}

// ---- the V1 acts, in the V1 slot ---------------------------------------------------------------------------------------

type ReasonCode = CandidateCloseAtSourceInput["reason_code"];
type WithdrawCode = "took_other_role" | "comp" | "location" | "timing" | "no_longer_interested" | "unreachable" | "other";
type HoldCode = "stronger_candidates_first" | "open_question" | "capacity_alternate" | "other";
type MessageKind = "invitation" | "itinerary" | "exercise_brief" | "closing_note" | "other";

const REASON_CODES = ["scope_mismatch", "experience_gap", "location", "comp_out_of_range", "not_a_fit_for_level", "internal_policy", "other"] as const;
const REASON_LABEL: Record<string, string> = { scope_mismatch: "Scope mismatch", experience_gap: "Experience gap", location: "Location", comp_out_of_range: "Comp out of range", not_a_fit_for_level: "Not a fit for the level", internal_policy: "Internal policy", other: "Other" };

/** V1's acts on the application and the records they write, with V1's markup and words (see the file header). */
function V1Acts({ rid, aid, a }: { rid: string; aid: string; a: ApplicationFull }) {
  const { can } = useSession();
  const c = a.card;
  const inval = [["application", aid], ["candidates", rid], ["requisition", rid], ["today"]] as const;
  const withdraw = useWrite((i: { requisition_id: string; application_id: string; reason_code: WithdrawCode; reason_text?: string | null }, k) => candidateWithdraw(i, k), inval);
  const reinstate = useWrite((i: { requisition_id: string; application_id: string; reason: string }, k) => candidateReinstate(i, k), inval);
  const hold = useWrite((i: { requisition_id: string; application_id: string; reason: HoldCode; reason_text?: string | null }, k) => candidateHold(i, k), inval);
  const release = useWrite((i: { requisition_id: string; application_id: string; outcome: "release" | "pass"; reason_text?: string | null }, k) => candidateRelease(i, k), inval);
  const early = useWrite((i: Parameters<typeof candidateDispositionEarly>[0], k) => candidateDispositionEarly(i, k), inval);
  const reverse = useWrite((i: { requisition_id: string; application_id: string; reason: string; told_check: boolean }, k) => candidateReverseDecision(i, k), inval);
  const logMsg = useWrite((i: { requisition_id: string; application_id: string; kind: MessageKind; sent_at: string; reference?: string | null; reply_logged?: boolean }, k) => candidateLogMessage(i, k), inval);
  const internal = useWrite((i: { requisition_id: string; application_id: string; internal: boolean }, k) => candidateMarkInternal(i, k), inval);
  const advance = useWrite((i: Parameters<typeof candidateDecideAdvance>[0], k) => candidateDecideAdvance(i, k), inval);
  const [dlg, setDlg] = useState<null | "withdraw" | "reinstate" | "hold" | "release" | "pass" | "early" | "reverse" | "message" | "adv_advance" | "adv_hold" | "adv_pass">(null);
  const [wCode, setWCode] = useState<WithdrawCode>("took_other_role");
  const [hCode, setHCode] = useState<HoldCode>("stronger_candidates_first");
  const [eCode, setECode] = useState<ReasonCode>("scope_mismatch");
  const [told, setTold] = useState(false);
  const [mKind, setMKind] = useState<MessageKind>("invitation");
  const [mRef, setMRef] = useState("");
  const err = withdraw.error ?? reinstate.error ?? hold.error ?? release.error ?? early.error ?? reverse.error ?? logMsg.error ?? internal.error;
  const base = { requisition_id: rid, application_id: aid };
  return (
    <div>
      <div className="hrow">
        <div className="acts application-actions">
          {a.state_archive === "live" && c.stage === "hm_assessed" && can("candidate.decide_advance") && (
            <>
              <button className="btn btn--primary" onClick={() => setDlg("adv_advance")}>Advance to the panel</button>
              <button className="btn" onClick={() => setDlg("adv_hold")}>Hold after this round</button>
              <button className="btn" onClick={() => setDlg("adv_pass")}>Pass after this round</button>
            </>
          )}
          <div className="acts action-tools">
            {/* two holds can be offered at once and they are two different writes: `candidate.hold` parks the application
                wherever it stands, while the advance decision above holds the candidate as the Hiring Manager's verdict on
                their own round. Each says its scope. */}
            {c.can["hold"] && <button className="btn btn--quiet" onClick={() => setDlg("hold")}>Hold candidate</button>}
            {c.can["release"] && (
              <>
                <button className="btn btn--primary" onClick={() => setDlg("release")}>Release</button>
                <button className="btn" onClick={() => setDlg("pass")}>Pass</button>
              </>
            )}
            {c.can["withdraw"] && <button className="btn" onClick={() => setDlg("withdraw")}>Withdrew</button>}
            {c.can["reinstate"] && <button className="btn btn--primary" onClick={() => setDlg("reinstate")}>Reinstate</button>}
            {c.can["disposition_early"] && c.stage !== "held" && <button className="btn btn--quiet" onClick={() => setDlg("early")}>Not selected</button>}
            {c.can["reverse"] && <button className="btn btn--quiet" onClick={() => setDlg("reverse")}>Reverse last decision</button>}
            {c.can["log_message"] && <button className="btn" onClick={() => setDlg("message")}>Log a message</button>}
            {(c.stage === "screen" || c.stage === "screened") && (
              <a className="btn btn--primary" href={`#requisitions/${rid}/candidates/${aid}/rounds/1`}>Recruiter Screen</a>
            )}
            {(c.stage === "hm_round" || c.stage === "hm_assessed") && (
              <a className={c.stage === "hm_assessed" ? "btn btn--quiet" : "btn btn--primary"} href={`#requisitions/${rid}/candidates/${aid}/rounds/2`}>Hiring Manager interview</a>
            )}
            {(c.stage === "panel" || c.stage === "debrief" || c.stage === "offer_approved" || c.stage === "alternate") && (
              <a className="btn" href={`#requisitions/${rid}/summary/${aid}`}>Summary Sheet</a>
            )}
            {(c.stage === "offer_approved" || c.stage === "offer" || c.stage === "hired" || c.stage === "declined" || c.stage === "reneged") && (
              <a className="btn btn--primary" href={`#requisitions/${rid}/offer/${aid}`}>Offer & close</a>
            )}
            <RecordAfterFact id={rid} family="requisition" section="candidates" references={{ application_id: aid }} />
          </div>
        </div>
      </div>
      <Banner error={err} />
      <div className="record-grid record-grid--start">
        <section className="card">
          <KV
            wide={c.disposition_status === "obligation_open" ? ["Disposition"] : []}
            items={[
              ["Furthest stage", c.furthest_stage_label],
              ["Batch", c.batch_no ? `Batch ${c.batch_no}` : MISSING],
              ["Disposition", c.disposition_status === "obligation_open" ? <V1Pill tone="warn">code and date due {a.disposition?.["due_at"] ? `by ${fmtDate(String(a.disposition["due_at"]))}` : ""}</V1Pill> : c.disposition_status === "recorded" ? `recorded · ${dispositionWord(a.disposition)}` : MISSING],
            ]}
          />
          <ResumeSection rid={rid} aid={aid} candidate={c.token} application={a} />
          {a.projection === "identity" && <RequirementsSection rid={rid} aid={aid} name={nameOf(c)} reads={c.requirements_met} canQualify={!!c.can["qualify"] && a.state_archive === "live"} />}
        </section>
        <section className="card">
          <h2 className="sec">Decisions <span className="sec__n">{a.decisions.length}</span></h2>
          {a.decisions.length === 0 ? (
            <Empty heading="Awaiting a decision">No decision yet.</Empty>
          ) : (
            <ol className="history">
              {a.decisions.map((d) => (
                <li key={String(d["id"])}>
                  <b>{String(d["kind"]).replace(/_/g, " ")}: {String(d["value"])}</b> <span className="muted">{fmtDate(String(d["at"]), true)}</span>
                  {d["supersedes"] ? <V1Pill tone="muted">supersedes</V1Pill> : null}
                  {d["reason"] ? <div className="muted">{String(d["reason"])}</div> : null}
                </li>
              ))}
            </ol>
          )}
          {a.holds.length > 0 && (
            <>
              <h3 className="sec">Holds</h3>
              <ul className="history">
                {a.holds.map((hd) => (
                  <li key={String(hd["id"])}>
                    {String(hd["reason"]).replace(/_/g, " ")} · {fmtDate(String(hd["placed_at"]))} → {hd["released_at"] ? `${fmtDate(String(hd["released_at"]))} (${String(hd["outcome"])})` : `review by ${fmtDate(String(hd["review_due_at"]))}`}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
        <section className="card">
          <h2 className="sec">Messages logged</h2>
          {a.messages.length === 0 ? (
            <Empty heading="No candidate communications yet">Messages to the candidate go through the ATS and are logged here.</Empty>
          ) : (
            <ul className="history">
              {a.messages.map((m) => (
                <li key={String(m["id"])}>
                  <b>{String(m["kind"]).replace(/_/g, " ")}</b> <span className="muted">{fmtDate(String(m["sent_at"]), true)}</span> {m["reference"] ? <span className="mono">{String(m["reference"])}</span> : null} {m["reply_logged_at"] ? <V1Pill tone="ok">replied</V1Pill> : String(m["kind"]) === "invitation" ? <V1Pill tone="warn">awaiting reply</V1Pill> : null}
                </li>
              ))}
            </ul>
          )}
          {a.withdrawals.length > 0 && (
            <>
              <h3 className="sec">Withdrawals</h3>
              <ul className="history">
                {a.withdrawals.map((w) => (
                  <li key={String(w["id"])}>
                    {String(w["reason_code"]).replace(/_/g, " ")} from {String(w["from"]).replace(/_/g, " ")} · {fmtDate(String(w["at"]))} {w["reinstated_at"] ? <V1Pill tone="ok">reinstated {fmtDate(String(w["reinstated_at"]))}</V1Pill> : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
      <section style={{ marginTop: 14 }}>
        <h2 className="sec">Rounds</h2>
        <Table label="Rounds" mobile="stack" head={<tr><th>Round</th><th>State</th><th>When</th><th>Interviewer</th><th>Packet status</th><th>Scorecard status</th></tr>}>
          {a.rounds.length === 0 ? <tr><td colSpan={6}>No rounds booked yet.</td></tr> : a.rounds.map((round) => {
            const n = round.round_kind === "recruiter_screen" ? "1" : round.round_kind === "hm_round" ? "2" : round.round_def_id;
            const statusLabel = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ");
            return <tr key={round.id}>
              <td><a href={`#requisitions/${rid}/candidates/${aid}/rounds/${n}`}>{round.label}</a></td>
              <td>{statusLabel(round.status)}</td>
              <td>{round.start_at ? fmtDate(round.start_at, true) : "Not scheduled"}</td>
              <td>{round.interviewer ?? "Not assigned"}</td>
              <td>{round.packet_status ? statusLabel(round.packet_status) : "Not generated"}</td>
              <td>{statusLabel(round.scorecard_status)}</td>
            </tr>;
          })}
        </Table>
      </section>
      {c.can["log_message"] && !c.internal && (
        <p className="muted">
          <button className="btn btn--sm" disabled={internal.isPending} onClick={() => void internal.mutateAsync({ ...base, internal: true })}>Flag as internal / referral</button>{" "}
          The intake's internal-candidate policy then applies; the Hiring Manager and HR Business Partner are told.
        </p>
      )}

      <ReasonDialog title="The candidate withdrew" label="Note (optional)" optional confirm="Record" open={dlg === "withdraw"} onClose={() => setDlg(null)} busy={withdraw.isPending} error={withdraw.error} onConfirm={(t) => void withdraw.mutateAsync({ ...base, reason_code: wCode, reason_text: t || null }).then(() => setDlg(null))}>
        <Field label="Reason" required id="w-code" hint="Booked rounds are cancelled and interviewers told; the candidate_withdrew disposition is written automatically">
          <select id="w-code" value={wCode} onChange={(e) => setWCode(e.target.value as WithdrawCode)}>
            {(["took_other_role", "comp", "location", "timing", "no_longer_interested", "unreachable", "other"] as const).map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Reinstate" label="Why" confirm="Reinstate" open={dlg === "reinstate"} onClose={() => setDlg(null)} busy={reinstate.isPending} error={reinstate.error} onConfirm={(reason) => void reinstate.mutateAsync({ ...base, reason }).then(() => setDlg(null))} />
      <ReasonDialog title="Hold the candidate" label="Note (optional)" optional confirm="Hold" open={dlg === "hold"} onClose={() => setDlg(null)} busy={hold.isPending} error={hold.error} onConfirm={(t) => void hold.mutateAsync({ ...base, reason: hCode, reason_text: t || null }).then(() => setDlg(null))}>
        <Field label="Reason" required id="h-code" hint="A five-business-day review keeps nobody parked silently">
          <select id="h-code" value={hCode} onChange={(e) => setHCode(e.target.value as HoldCode)}>
            <option value="stronger_candidates_first">Stronger candidates first</option>
            <option value="open_question">Open question</option>
            <option value="capacity_alternate">Alternate for capacity</option>
            <option value="other">Other</option>
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Release from hold" label="Note (optional)" optional confirm="Release" open={dlg === "release"} onClose={() => setDlg(null)} busy={release.isPending} error={release.error} onConfirm={(t) => void release.mutateAsync({ ...base, outcome: "release", reason_text: t || null }).then(() => setDlg(null))} />
      <ReasonDialog title="Pass from hold" label="Why" confirm="Pass" open={dlg === "pass"} onClose={() => setDlg(null)} busy={release.isPending} error={release.error} onConfirm={(t) => void release.mutateAsync({ ...base, outcome: "pass", reason_text: t }).then(() => setDlg(null))} />
      <ReasonDialog title="Not selected" label="Note (optional)" optional confirm="Record" open={dlg === "early"} onClose={() => setDlg(null)} busy={early.isPending} error={early.error} onConfirm={(t) => void early.mutateAsync({ ...base, reason_code: eCode, note: t || null }).then(() => setDlg(null))}>
        <Field label="Reason code" required id="e-code" hint="Booked rounds are cancelled; the disposition obligation opens now">
          <select id="e-code" value={eCode} onChange={(e) => setECode(e.target.value as ReasonCode)}>
            {REASON_CODES.map((k) => <option key={k} value={k}>{REASON_LABEL[k]}</option>)}
          </select>
        </Field>
      </ReasonDialog>
      <ReasonDialog title="Reverse the last decision" label="Why" confirm="Reverse" open={dlg === "reverse"} onClose={() => { setDlg(null); setTold(false); }} busy={reverse.isPending} error={reverse.error} onConfirm={(reason) => void reverse.mutateAsync({ ...base, reason, told_check: told }).then(() => { setDlg(null); setTold(false); })}>
        <label className="check">
          <input type="checkbox" checked={told} onChange={(e) => setTold(e.target.checked)} /> Nothing has reached the candidate from this application (required)
        </label>
      </ReasonDialog>
      <ReasonDialog title="Advance to the panel" label="Why" confirm="Advance" open={dlg === "adv_advance"} onClose={() => setDlg(null)} busy={advance.isPending} error={advance.error} onConfirm={(reason_text) => void advance.mutateAsync({ ...base, value: "advance", reason_text }).then(() => setDlg(null))}>
        <p className="muted">One planned interview per onsite round in use; the exercise step opens when the intake requires one. Refused when any submitted scorecard fired the two-2s rule.</p>
      </ReasonDialog>
      <ReasonDialog title="Hold after this round" label="Why" confirm="Hold" open={dlg === "adv_hold"} onClose={() => setDlg(null)} busy={advance.isPending} error={advance.error} onConfirm={(reason_text) => void advance.mutateAsync({ ...base, value: "hold", reason_text }).then(() => setDlg(null))} />
      <ReasonDialog title="Pass after this round" label="Why" confirm="Pass" open={dlg === "adv_pass"} onClose={() => setDlg(null)} busy={advance.isPending} error={advance.error} onConfirm={(reason_text) => void advance.mutateAsync({ ...base, value: "pass", reason_text, reason_code: "not_a_fit_for_level" }).then(() => setDlg(null))} />
      <ReasonDialog title="Log a message sent from the ATS" label="Note (optional)" optional confirm="Log" open={dlg === "message"} onClose={() => setDlg(null)} busy={logMsg.isPending} error={logMsg.error} onConfirm={() => void logMsg.mutateAsync({ ...base, kind: mKind, sent_at: new Date().toISOString(), reference: mRef || null }).then(() => { setDlg(null); setMRef(""); })}>
        <Field label="Kind" required id="m-kind" hint="An invitation starts the two-business-day reply timer">
          <select id="m-kind" value={mKind} onChange={(e) => setMKind(e.target.value as MessageKind)}>
            <option value="invitation">Invitation</option>
            <option value="itinerary">Itinerary</option>
            <option value="exercise_brief">Exercise brief</option>
            <option value="closing_note">Closing note</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="ATS message reference" id="m-ref">
          <input id="m-ref" value={mRef} onChange={(e) => setMRef(e.target.value)} />
        </Field>
      </ReasonDialog>
    </div>
  );
}

/** V1's résumé versions and upload, as the application page offers them (identity and review projections). */
function ResumeSection({ rid, aid, candidate, application }: { rid: string; aid: string; candidate: string; application: ApplicationFull }): ReactNode {
  const { can, me } = useSession();
  const attach = useWrite((i: Parameters<typeof candidateAttachResume>[0], key) => candidateAttachResume(i, key), [["application", aid], ["candidates", rid]]);
  const [uploadKey, setUploadKey] = useState(0);
  const [file, setFile] = useState<FileUploadOutput | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<FilePhase>("idle");
  if (application.projection !== "identity" && application.projection !== "review") return null;
  const versions = (application.resume_versions ?? []) as unknown as FileVersion[];
  const rows = versions.map((version) => ({ ...version, pinned: version.pinned && !!application.resume_pinned_at }));
  if (application.resume_file_id && !rows.some((r) => r.file_id === application.resume_file_id)) rows.unshift({ file_id: application.resume_file_id, version: application.resume_version, pinned: !!application.resume_pinned_at });
  const closed = application.state_archive === "archived" || ["archived", "closed_cancelled", "closed_filled"].includes(application.requisition_status);
  const readonly = closed || !!me?.view_as;
  return <section className="file-section">
    <h3 className="sub">Résumé</h3>
    <FileList label="Résumé versions" files={rows} />
    {application.resume_pinned_at && <p className="muted">Review version pinned {fmtDate(application.resume_pinned_at, true)}. Later uploads do not change the batch.</p>}
    {application.state_archive !== "archived" && application.projection === "identity" && can("candidate.attach_resume") && <>
      <FileField key={uploadKey} compactIdle label="Upload a résumé version" owner={{ kind: "resume", owner_type: "candidate", owner_id: candidate, requisition_id: rid }} accept={DOCUMENT_TYPES} disabled={readonly || attach.isPending} disabledReason={closed ? "This requisition is closed. Files are read-only." : me?.view_as ? "Viewing as another person — read-only." : undefined} onChange={(next) => { attach.reset(); setFile(next); }} onBusy={setBusy} onPhase={setUploadPhase} />
      {uploadPhase !== "idle" && !attach.isSuccess && <button className="btn" disabled={!file || busy || readonly || attach.isPending} onClick={() => file && void attach.mutateAsync({ requisition_id: rid, application_id: aid, resume_file_id: file.file_id }).then(() => { setFile(null); setUploadKey((key) => key + 1); }).catch(() => undefined)}>Save résumé version</button>}
      <Banner error={attach.error} />
      {attach.isSuccess && <Receipt>Résumé version saved. Earlier review versions stay on the record.</Receipt>}
    </>}
  </section>;
}
